// The headers attribute: a list of [key, value, isSecure, wasEdited] tuples.
// `wasEdited` is UI-only — it marks an encrypted value the user has replaced, so
// the stored ciphertext is not shown back — and is stripped before saving.

const KEY = 0;
const VALUE = 1;
const SECURE = 2;

export const EMPTY_ENTRY = ['', '', false];

// The three inputs a row renders, which is also how its errors are keyed.
export const HEADER_PARTS = ['key', 'value', 'secure'];

// Older records stored headers as an object rather than a tuple array. Those are
// surfaced as empty so the user can fill them in again.
export const toEditableList = (value) => (Array.isArray(value) ? value : []);

export const addEntry = (list) => [...toEditableList(list), [...EMPTY_ENTRY]];

export const removeEntry = (list, index) => {
  const next = [...toEditableList(list)];
  next.splice(index, 1);
  return next;
};

export const changeEntry = (list, { index, item, part, value, checked }) => {
  const next = [...toEditableList(list)];

  switch (part) {
    case 'key':
      next[index] = [value, item[VALUE], item[SECURE]];
      break;
    case 'value':
      next[index] = [item[KEY], value, item[SECURE], true];
      break;
    case 'secure':
      // Unchecking encryption clears the value: the plaintext behind a stored
      // ciphertext is not available to echo back.
      next[index] = [item[KEY], checked ? item[VALUE] : '', checked, true];
      break;
    default:
      return list;
  }

  return next;
};

export const partOfHeaderInputName = (name) => String(name).split('-')[0];

// Row errors are addressed by position ('key-0', 'value-1'), so removing a row
// has to slide the ones below it up, or a surviving row inherits the warning of
// the row that was deleted.
export const reindexEntryErrors = (errors, removedIndex, remainingCount) => {
  const next = { ...errors };

  HEADER_PARTS.forEach((part) => {
    for (let i = removedIndex; i < remainingCount; i += 1) {
      next[`${part}-${i}`] = errors[`${part}-${i + 1}`] ?? null;
    }
    delete next[`${part}-${remainingCount}`];
  });

  return next;
};

export const isEntryFilled = (entry) =>
  String(entry[KEY] || '').trim() !== '' &&
  String(entry[VALUE] || '').trim() !== '';

export const isLastEntryBlank = (list) =>
  toEditableList(list)[toEditableList(list).length - 1]?.[KEY] === '';

// Drops the UI-only "edited" flag from every headers attribute before saving.
export const stripEditedFlag = (attributes = []) =>
  attributes.map((item) => {
    if (item.name.includes('headers') && Array.isArray(item.value)) {
      return { ...item, value: item.value.map((entry) => entry.slice(0, 3)) };
    }
    return item;
  });
